/**
 * OTP Attempt Tracker
 *
 * Persists OTP attempt count and lock state to localStorage instead of
 * Legend-State's IndexedDB store. This is necessary because `otpAttemptCount`
 * and `isOtpLocked` are NOT in module-core's initial `createPersistedStore`
 * state — they are dynamically added via TypeScript cast in module-auth.
 * Legend-State's `syncObservable` cannot reliably track/persist such fields,
 * leading to undefined reads and failed writes.
 *
 * localStorage is used because:
 * - Synchronous: no hydration race conditions
 * - Reliable: always available on client
 * - Survives page reload: prevents users from bypassing lockout
 * - Simple: no IndexedDB table registration needed
 */

const STORAGE_KEY = 'kp_otp_attempts';

interface OtpAttemptData {
    /** Number of incorrect OTP attempts in the current OTP session */
    count: number;
    /** Whether the OTP input is locked (max attempts or session exhausted) */
    locked: boolean;
    /** Timestamp (ms) when this OTP session started — used to auto-expire stale locks */
    sessionStartedAt: number;
}

/** Stale lock auto-expiry: 10 minutes. After this, old attempt data is ignored. */
const SESSION_EXPIRY_MS = 10 * 60 * 1000;

/**
 * Read current OTP attempt data from localStorage.
 * Returns defaults (count=0, locked=false) if absent or expired.
 */
export const getOtpAttemptData = (): OtpAttemptData => {
    if (typeof window === 'undefined') {
        return { count: 0, locked: false, sessionStartedAt: Date.now() };
    }

    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return { count: 0, locked: false, sessionStartedAt: Date.now() };
        }

        const data: OtpAttemptData = JSON.parse(raw);

        // Auto-expire stale sessions — prevents permanent lockout from orphaned data
        if (Date.now() - data.sessionStartedAt > SESSION_EXPIRY_MS) {
            localStorage.removeItem(STORAGE_KEY);
            return { count: 0, locked: false, sessionStartedAt: Date.now() };
        }

        return data;
    } catch {
        localStorage.removeItem(STORAGE_KEY);
        return { count: 0, locked: false, sessionStartedAt: Date.now() };
    }
};

/**
 * Increment the OTP attempt counter by 1 and persist.
 * @returns The new attempt count after increment.
 */
export const incrementOtpAttemptCount = (): number => {
    const data = getOtpAttemptData();
    data.count += 1;
    persistOtpAttemptData(data);
    return data.count;
};

/**
 * Lock the OTP input (max attempts exceeded or session exhausted).
 */
export const lockOtpAttempts = (): void => {
    const data = getOtpAttemptData();
    data.locked = true;
    persistOtpAttemptData(data);
};

/**
 * Reset all OTP attempt data — call when a new OTP is requested (initial or resend).
 * This starts a fresh session with count=0 and locked=false.
 */
export const resetOtpAttemptData = (): void => {
    persistOtpAttemptData({ count: 0, locked: false, sessionStartedAt: Date.now() });
};

/**
 * Write OTP attempt data to localStorage.
 */
const persistOtpAttemptData = (data: OtpAttemptData): void => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
        // localStorage quota exceeded or unavailable — fail silently.
        // Counter will work in-memory for this session via getOtpAttemptData fallback.
    }
};
